import cloneDeep from "lodash/cloneDeep";
import { connect } from "react-redux";
import { Form, getIn } from "formik";
import React, { useState } from "react";
import { default as ReactSelect } from "react-select";
import { withRouter } from "react-router-dom";
import * as yup from "yup";

import { api } from "@/APIRequests";
import Button from "@/components/UI/Button";
import { CustomForm } from "@/component-library/CustomForm";
import FormikInput from "@/components/UI/FormikInput";
import images from "@/utils/images";
import InputError from "@/components/UI/InputError";
import Label from "@/components/UI/Label";
import { showAlertWithAction } from "@/redux/actions/alerts";

const ConfirmInsurance = (props) => {
  const { setSharedWorkflowData, sharedWorkflowData } = props;

  const insuranceData =
    sharedWorkflowData.workflow_instance && sharedWorkflowData.workflow_instance.state;

  const [isEditing, setIsEditing] = useState(false);
  const [insuranceOptions, setInsuranceOptions] = useState([]);

  const confirmInsuranceHandler = async () => {
    try {
      const options = { params: { showLoader: true } };
      const data = {
        node_name: "confirm_insurance",
        state: {
          insurance_confirmed: true,
        },
        workflow_instance_sort_key: sharedWorkflowData.workflow_instance.sort_key,
      };
      const headers = {
        "X-On-Behalf-Of": sharedWorkflowData.workflow_instance.entity_id,
      };
      const response = await api.workflow.update_state_and_get_next_node({
        data,
        headers,
        options,
      });

      setSharedWorkflowData({
        ...sharedWorkflowData,
        workflow_instance: response.workflow_instance,
      });
    } catch (e) {
      console.log(e);
      props.showAlertWithAction("Error confirming insurance.");
    }
  };

  const submitHandler = async (formData, actions) => {
    try {
      const options = { params: { showLoader: true } };
      const data = {
        dob: sharedWorkflowData.workflow_instance.state.dob,
        email: sharedWorkflowData.workflow_instance.state.email,
        insurance_data: { ...formData },
        workflow_instance_sort_key: sharedWorkflowData.workflow_instance.sort_key,
      };
      const headers = {
        "X-On-Behalf-Of": sharedWorkflowData.workflow_instance.entity_id,
      };
      const response = await api.shared.update_insurance({ data, headers, options });

      setSharedWorkflowData({
        ...sharedWorkflowData,
        workflow_instance: response.workflow_instance,
      });
      setIsEditing(false);
    } catch (e) {
      console.log(e);
      props.showAlertWithAction("Error saving insurance information");
    }
  };

  const updateInsuranceHandler = async () => {
    try {
      const options = { params: { showLoader: true } };
      // TODO: Consider migrating to useInsuranceList hook
      // See MemberProfileMedical.js for reference implementation
      const rawInsuranceList = await api.patient.fetch_insurance_list({ options });

      if (rawInsuranceList && rawInsuranceList.carriers) {
        setInsuranceOptions(
          rawInsuranceList.carriers.map((carrier) => {
            return { label: carrier, value: carrier };
          }),
        );
        setIsEditing(true);
      }
    } catch (e) {
      console.log(e);
      props.showAlertWithAction("Error fetching insurance providers");
    }
  };

  const renderForm = (formikProps) => {
    const formikInputProps = {
      errors: formikProps.errors,
      inputClasses: "Input--sm",
      touched: formikProps.touched,
    };

    return (
      <div>
        <Form>
          <div className="workflow-container-sm workflow-container-border mb-5">
            <div className="p-lg-5 p-3">
              <div className="row">
                <div className="col-12 mb-3">
                  <div style={{ margin: "0 auto" }}>
                    <Label>Insurance Provider</Label>
                    <ReactSelect
                      className="react-select-box text-left"
                      classNamePrefix="react-select-box"
                      defaultValue={
                        insuranceData.carrier
                          ? {
                              label: insuranceData.carrier,
                              value: insuranceData.carrier,
                            }
                          : null
                      }
                      isClearable={true}
                      isSearchable={true}
                      name="carrier"
                      onChange={async (option, action) => {
                        if (option) {
                          await formikProps.setFieldValue("carrier", option.value);
                        } else {
                          await formikProps.setFieldValue("carrier", "");
                        }
                        formikProps.setFieldTouched("carrier");
                      }}
                      options={insuranceOptions}
                    />
                    {getIn(formikProps.errors, "carrier") &&
                    getIn(formikProps.touched, "carrier") ? (
                      <InputError classes="custom-error" style={{ position: "relative" }}>
                        {getIn(formikProps.errors, "carrier")}
                      </InputError>
                    ) : null}
                  </div>
                </div>

                <div className="col-12">
                  <FormikInput
                    {...formikInputProps}
                    formEl={{
                      elementConfig: {
                        label: "Member ID",
                        placeholder: "Enter Member ID",
                        type: "text",
                      },
                      elementType: "input",
                      name: "member_id",
                    }}
                  />
                </div>

                <div className="col-12">
                  <FormikInput
                    {...formikInputProps}
                    formEl={{
                      elementConfig: {
                        label: "Group ID",
                        placeholder: "Enter Group ID",
                        type: "text",
                      },
                      elementType: "input",
                      name: "group_id",
                    }}
                  />
                </div>

                <div className="col-12">
                  <FormikInput
                    {...formikInputProps}
                    formEl={{
                      elementConfig: {
                        label: "Primary Insurance Holder",
                        placeholder: "Enter Primary Insurance Holder",
                        type: "text",
                      },
                      elementType: "input",
                      name: "policy_holder_name",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="workflow-container-sm d-flex justify-content-between">
            <Button
              className="Btn Btn--otl-gry Btn--sm-2 workflow-btn"
              onClick={() => setIsEditing(false)}
              type="button"
            >
              Cancel
            </Button>
            <Button className="Btn Btn--pri Btn--sm-2 workflow-btn" type="submit">
              Save
            </Button>
          </div>
        </Form>
      </div>
    );
  };

  return (
    <div className="p-lg-5 p-3">
      <div className="fs-22 txt-pri mb-5 text-center tw:font-bold">
        Confirm your insurance information
      </div>
      <div className="text-center my-4">
        <img
          alt="Confirm Insurance"
          className="signup-progress-bar"
          src={images("./common/workflow/ed-workflow-1-confirm-ins-active.png")}
        />
      </div>
      {sharedWorkflowData && sharedWorkflowData.workflow_instance && (
        <>
          {!isEditing && (
            <>
              <div className="workflow-container-sm workflow-container-border mb-5">
                <div className="p-lg-5 p-3">
                  <div className="row">
                    <div className="col-12 mb-4">
                      <div className="txt-gry fs-14">Insurance Provider</div>
                      <div className="fs-md-16 fs-14 tw:font-bold">{insuranceData.carrier}</div>
                    </div>
                    <div className="col-12 mb-4">
                      <div className="txt-gry fs-14">Member ID</div>
                      <div className="fs-md-16 fs-14 tw:font-bold">{insuranceData.member_id}</div>
                    </div>
                    <div className="col-12 mb-4">
                      <div className="txt-gry fs-14">Group ID</div>
                      <div className="fs-md-16 fs-14 tw:font-bold">{insuranceData.group_id}</div>
                    </div>
                    <div className="col-12 mb-1">
                      <div className="txt-gry fs-14">Policy Insurance Holder</div>
                      <div className="fs-md-16 fs-14 tw:font-bold">
                        {insuranceData.policy_holder_name}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="workflow-container-sm d-flex justify-content-between">
                <Button
                  className="Btn Btn--otl-gry Btn--sm-2 workflow-btn"
                  onClick={() => props.backHandler()}
                  type="button"
                >
                  Back
                </Button>
                <Button
                  className="Btn Btn--pri Btn--sm-2 workflow-btn"
                  onClick={confirmInsuranceHandler}
                  type="button"
                >
                  Confirm & Continue
                </Button>
              </div>
              <div className="text-center my-5 fs-14">
                <Button
                  className="Btn Btn--otl-pri Btn--sm-2 workflow-btn workflow-btn-lg"
                  onClick={updateInsuranceHandler}
                  style={{ padding: "0 12px" }}
                  type="button"
                >
                  Update Insurance Information
                </Button>
              </div>
            </>
          )}
          {isEditing && (
            <>
              <CustomForm
                initialValues={cloneDeep(insuranceData)}
                onSubmit={submitHandler}
                render={renderForm}
                validationSchema={insuranceValidation}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default connect(null, { showAlertWithAction })(withRouter(ConfirmInsurance));

const insuranceValidation = yup.object().shape({
  carrier: yup.string().required("Insurance provider is required").nullable(),
  member_id: yup
    .string()
    .required("Member id is required")
    .matches(/^[a-zA-Z0-9]*$/, "Enter a valid member id")
    .nullable(),
  policy_holder_name: yup
    .string()
    .matches(/^[a-z A-Z-]+$/, "Numerals, spaces & special characters are not allowed")
    .required("Policy holder name is required")
    .nullable(),
});
